<script lang="ts">
  import type { Alternative } from '@pin/schemas/client-state-response';
  import { onMount } from 'svelte';
  import { link } from 'svelte-spa-router';
  import { getItem, removeItem } from '../../util/storage';
  import { clientState } from '../../util/stores';

  export let artist: Alternative;
  export let reverse: boolean;
  export let votedArtistId: string = '';
  export let submitted = false;

  let infoRef: HTMLDivElement | undefined;
  let focusRef: HTMLAnchorElement | undefined;

  const getImage = (votedFor: boolean, artist: Alternative) => {
    if (votedFor) {
      return {
        src: artist.extraData.thanksImage.src,
        fallbackSrc: artist.extraData.thanksImage.fallbackSrc,
        alt: `${artist.extraData.thanksImage.alt}. Bildet ligger på en gullforgylt bakgrunn`,
      };
    }
    return artist.image;
  };
  const focusId = `choose-${artist.id}`;
  $: voted = !!votedArtistId;
  $: selected = votedArtistId === artist.id;
  $: image = getImage(selected, artist);
  $: bgHeight = infoRef?.offsetHeight != null ? infoRef.offsetHeight / 16 : 0;
  $: marginTop = bgHeight > 5 ? `calc(${bgHeight}rem * 0.2)` : '1rem';

  onMount(() => {
    if (getItem('session', 'list-focus-id') === focusId) {
      if (focusRef != null) {
        focusRef?.focus({ preventScroll: false });
      } else {
        infoRef?.focus({ preventScroll: false });
      }
      removeItem('session', 'list-focus-id');
    }
  });
</script>

<svelte:head>
  {#if artist}
    <link rel="preload" as="image" href={artist.image.src} />
  {/if}
</svelte:head>

<li
  class="card"
  class:reverse
  class:voted
  class:selected
  style={`--bg-height: ${bgHeight}rem; --margintop: ${marginTop}`}
>
  {#if selected}
    <div class="voted-bg" />
  {/if}
  <div
    bind:this={infoRef}
    class="info"
    class:info-reverse={reverse}
    tabindex="-1"
  >
    <h3>{artist.title}</h3>
    <span class="songtitle">{artist.description}</span>
  </div>
  <div class="bg" />
  <div class="link">
    <picture class:imgbehindbutton={!submitted}>
      <source srcset={image.src} type="image/webp" />
      <source srcset={image.fallbackSrc} type="image/png" />
      <img
        class:votedimage={selected}
        src={image.fallbackSrc}
        alt={image.alt}
      />
    </picture>
    {#if !submitted}
      <a
        href="/artist/{artist.id}"
        use:link
        id={focusId}
        bind:this={focusRef}
        class:disabled={$clientState.type === 'before'}
        aria-label={`Velg ${artist.title}`}
        class="select-artist secondary-button secondary-button-link"
      >
        Velg
      </a>
    {/if}
  </div>
</li>

<style>
  li {
    width: 100%;
    max-width: 28rem;
  }
  .card {
    transform: translateZ(0);
    display: flex;
    align-items: flex-end;
    flex-direction: row-reverse;
    position: relative;
    margin-left: 1.25rem;
    --inline: 0.375rem;
    margin-top: var(--margintop);
  }

  .card.reverse {
    margin-left: 0rem;
    margin-right: 1.25rem;
    flex-direction: row;
  }

  .bg {
    position: absolute;
    width: 100%;
    min-height: 50%;
    height: calc(var(--bg-height) + 2rem);
    z-index: -1;
    background: linear-gradient(90deg, #4e1b52 0%, #1a163e 100%);
    clip-path: polygon(
      calc(0% + var(--inline)) calc(16% + var(--inline)),
      calc(100% - var(--inline)) calc(0% + var(--inline)),
      calc(100% - var(--inline)) calc(100% - var(--inline)),
      calc(0% + var(--inline)) calc(100% - var(--inline))
    );
  }

  .reverse .bg {
    background: linear-gradient(90deg, #1a163e 0%, #4e1b52 100%);
    clip-path: polygon(
      calc(0% + var(--inline)) calc(0% + var(--inline)),
      calc(100% - var(--inline)) calc(16% + var(--inline)),
      calc(100% - var(--inline)) calc(100% - var(--inline)),
      calc(0% + var(--inline)) calc(100% - var(--inline))
    );
  }

  .voted-bg {
    position: absolute;
    width: 100%;
    min-height: 50%;
    height: calc(var(--bg-height) + 2rem);
    z-index: -1;
    background: #9a7700;
    opacity: 0.68;
    clip-path: polygon(0% 16%, 100% 0%, 100% 100%, 0% 100%);
  }

  .reverse .voted-bg {
    clip-path: polygon(0% 0%, 100% 16%, 100% 100%, 0% 100%);
  }

  .selected .bg {
    background: linear-gradient(
      180deg,
      #683b10 5%,
      #8e6807 25%,
      #bb9725 60%,
      #e1b828 80%
    );
  }

  .imgbehindbutton {
    margin-bottom: 2rem;
  }

  .link {
    position: relative;
  }

  .voted .link {
    padding-bottom: var(--inline);
  }

  img {
    display: block;
    width: 10rem;
    max-width: 10rem;
    height: 10rem;
  }

  img.votedimage {
    width: 10rem;
    height: 10rem;
  }

  .select-artist {
    display: block;
    position: absolute;
    bottom: 0rem;
    left: 0;
    right: 0;
    width: 100%;
    font-weight: 890;
    letter-spacing: 0.2rem;
    text-transform: uppercase;
    margin: 0;
  }

  .info {
    flex: 1;
    padding: 1.5rem 1.5rem 1.25rem 1rem;
    text-align: left;
    min-height: 40%;
  }

  .info:focus {
    box-shadow: none;
  }

  .info-reverse {
    padding: 1.5rem 1rem 1.25rem 1.5rem;
  }

  h3 {
    font-size: 0.9375rem;
    font-weight: 700;
  }

  .selected h3 {
    color: #402400;
  }

  h3:focus {
    box-shadow: none;
  }

  .selected .songtitle {
    color: #402400;
  }

  .songtitle {
    display: block;
    font-size: 0.8125rem;
    font-weight: 560;
    line-height: 1;
    letter-spacing: 0.02em;
    margin-top: 0.25rem;
    opacity: 0.8;
  }
  a {
    text-align: center;
    background: linear-gradient(89.81deg, #7d4589 0.18%, #d13a6c 99.85%);
    font-weight: 700;
    font-size: 0.9375rem;
    line-height: 1.25;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 1rem 2rem;
  }

  a.disabled {
    background: linear-gradient(181.84deg, #0d0b20 1.55%, #1a163e 187.27%);
    color: rgba(255, 255, 255, 0.7);
  }

  @media (max-width: 22rem) {
    .card,
    .card.reverse {
      flex-direction: column-reverse;
      align-items: center;
    }
  }
</style>
